import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { orderSectionData } from "../../../redux/slices/orgAdmin/promotionsSlice";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Pagination from "@mui/material/Pagination";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchLanguages } from "../../../redux/slices/superAdmin/languagesSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CollectionsIcon from "@mui/icons-material/Collections";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { buildMedia } from "../../Utils/AuthService";
import Attachments from "./Utils/Attachments";
import GalleryUpload from "./Utils/GalleryUpload";
import { rowsPerPage } from "../../../Constants";
import { listTags } from "../../../redux/slices/orgAdmin/tagsOrgSlice";
import Tags from "./Components/Tags";

import {
  StyledButton,
  StyledContainer,
  StyledTableCell,
} from "../../CSS/Styles";
import {
  fetchEvents,
  saveEvent,
  getEvent,
} from "../../../redux/slices/orgAdmin/eventSlice";
import { deleteSectionData } from "../../../redux/slices/orgAdmin/promotionsSlice";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function Team() {
  const dispatch = useDispatch();
  const { eventsList, loading } = useSelector((state) => state.events);
  const { languagesList } = useSelector((state) => state.languages);
  const { tags } = useSelector((state) => state.tags);
  const { companyId } = useSelector((state) => state.app);
  const [deleteTitle, setDeleteTitle] = useState("");
  const [media, setMedia] = useState(null);
  const [updatedEvent, setUpdatedEvent] = useState({
    en: { title: "", brief: "", location: "", description: "", yt_url: "" },
    fr: { title: "", brief: "", location: "", description: "", yt_url: "" },
    ar: { title: "", brief: "", location: "", description: "", yt_url: "" },
    date: null,
    attachments: {
      mainImage: [],
      report: [],
    },
  });

  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [galleryId, setGalleryId] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionIdToDelete, setPromotionIdToDelete] = useState(null);
  const [localNews, setLocalNews] = useState([]);

  const [page, setPage] = useState(1);
  const totalRecords =
    eventsList?.pagination?.totalRecords || eventsList.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const files = {};

  useEffect(() => {
    dispatch(fetchEvents({ companyId, page, limit: rowsPerPage }));
    dispatch(fetchLanguages());
    dispatch(listTags({ companyId }));
  }, [dispatch, companyId, page]);

  const sectionId = 2;

  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [newEvent, setNewEvent] = useState({
    en: { title: "", brief: "", location: "", description: "", yt_url: "" },
    fr: { title: "", brief: "", location: "", description: "", yt_url: "" },
    ar: { title: "", brief: "", location: "", description: "", yt_url: "" },
    date: null,
    attachments: {
      mainImage: [],
      report: [],
    },
  });

  const [rowToEdit, setRowToEdit] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const handleInputChange = (language, field, value) => {
    setNewEvent((prev) => ({
      ...prev,
      [language]: {
        ...prev[language],
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (eventsList?.data) {
      setLocalNews(eventsList.data);
    }
  }, [eventsList]);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    // Reorder the items in local state
    const reorderedItems = Array.from(localNews);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    // Update local state for immediate UI feedback
    setLocalNews(reorderedItems);

    // Prepare the payload for dispatch with the reordered data
    const reorderedData = reorderedItems?.map((item, index) => ({
      id: item.id,
      sortIndex: index + 1, // Assuming that you want to track the index for sorting
    }));

    // Dispatch the action to persist changes in the order
    dispatch(orderSectionData(reorderedData))
      .unwrap()
      .then(() => {
        dispatch(fetchEvents({ companyId, page, limit: rowsPerPage }));
      })
      .catch((error) => {
        console.error("Error updating order:", error);
      });
  };

  const handleDeleteMainImage = (isDeleted) => {
    setNewEvent((prev) => ({
      ...prev,
      deleteMainImage: isDeleted, // ✅ Dynamically update promotionData
    }));
  };

  const handleDeleteAttachment = (language, attachmentId) => {
    setNewEvent((prev) => {
      const updatedDeletedAttachments = new Set(prev.deletedAttachments || []);

      // Add language to the Set if an attachment was deleted
      updatedDeletedAttachments.add(language);

      return {
        ...prev,
        deletedAttachments: Array.from(updatedDeletedAttachments), // Convert Set back to Array
      };
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewEvent((prev) => ({ ...prev, attachment: file }));
    }
  };

  const handleEditEvent = async (id) => {
    try {
      setRowToEdit(id);

      const actualId = id && typeof id === "object" ? id.id : id;

      const eventData = await dispatch(getEvent({ id: actualId })).then(
        (res) => res.payload
      );

      if (
        eventData?.status === "error" &&
        eventData.message === "Record not found"
      ) {
        alert("Record not found.");
        return;
      }
      if (!eventData || !eventData.data) return;

      const eventId = eventData.data?.id;
      const mainImage = eventData.data?.mainImage || "";

      const attachments = Object.keys(eventData.data)
        .filter(
          (lang) =>
            ["en", "fr", "ar"].includes(lang) &&
            eventData.data[lang]?.attachment
        )
        .map((lang) => ({
          languageId: lang,
          fileName: eventData.data[lang].attachment,
        }));

      const mediaData = buildMedia(companyId, eventId, mainImage, attachments);

      setMedia(mediaData);

      // Handle existing tags - similar to NewsLetterPolicyBriefs
      const availableLanguages = ["en", "fr", "ar"];
      const defaultLanguage = "en"; // You might want to get this from languagesList like in NewsLetterPolicyBriefs
      
      const tag_ids = eventData.data?.[defaultLanguage]?.tag_ids || [];
      const tag_names = eventData.data?.[defaultLanguage]?.tag_names || [];

      const selectedTagsFromResponse = tag_ids.map((id, index) => ({
        id,
        tag_name: tag_names[index] || "",
      }));

      setSelectedTags(selectedTagsFromResponse);

      const prefilledEvent = {
        en: {
          title: eventData.data?.en?.title || "",
          brief: eventData.data?.en?.brief || "",
          location: eventData.data?.en?.location || "",
          description: eventData.data?.en?.description || "",
          yt_url: eventData.data?.en?.yt_url || "",
        },
        fr: {
          title: eventData.data?.fr?.title || "",
          brief: eventData.data?.fr?.brief || "",
          location: eventData.data?.fr?.location || "",
          description: eventData.data?.fr?.description || "",
          yt_url: eventData.data?.fr?.yt_url || "",
        },
        ar: {
          title: eventData.data?.ar?.title || "",
          brief: eventData.data?.ar?.brief || "",
          location: eventData.data?.ar?.location || "",
          description: eventData.data?.ar?.description || "",
          yt_url: eventData.data?.ar?.yt_url || "",
        },
        date: eventData.data?.en.date
          ? eventData.data.en.date.replace(" ", "T").slice(0, 16)
          : "",
        attachments: mediaData,
        deleteMainImage: false,
        deletedAttachments: [],
      };

      setNewEvent(prefilledEvent);
      setCurrentLanguage("en");
      setIsAddingTeam(true);
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  };

  const handleAddEvent = () => {
    setRowToEdit(null);
    setMedia(null);
    setSelectedTags([]);
    setNewEvent({
      en: { title: "", brief: "", location: "", description: "", yt_url: "" },
      fr: { title: "", brief: "", location: "", description: "", yt_url: "" },
      ar: { title: "", brief: "", location: "", description: "", yt_url: "" },
      date: null,
      // attachments: null,
    });
    setCurrentLanguage("en");
    setIsAddingTeam(true);
  };

  const handleSaveLanguage = () => {
    if (currentLanguage === "ar") {
      handleFinalizeEvent();
    } else {
      setConfirmationDialogOpen(true);
    }
  };

  const handleGlobalChange = (field, value) => {
    setNewEvent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProceedToNextLanguage = () => {
    setConfirmationDialogOpen(false);

    if (currentLanguage === "en") {
      setCurrentLanguage("fr");
    } else if (currentLanguage === "fr") {
      setCurrentLanguage("ar");
    }
  };

  const handleAttachmentsChange = (newAttachments) => {
    setNewEvent((prevNews) => {
      const prevAttachments = prevNews.attachments || {};

      return {
        ...prevNews,
        attachments: {
          ...prevAttachments,
          [`attachment_${currentLanguage}`]:
            newAttachments.report ??
            prevAttachments[`attachment_${currentLanguage}`],

          mainImage: newAttachments.hasOwnProperty("mainImage")
            ? newAttachments.mainImage
            : (prevAttachments.mainImage ?? null),
        },
      };
    });
  };

  const handleFinalizeEvent = () => {
    const payload = rowToEdit
      ? { id: typeof rowToEdit === "object" ? rowToEdit.id : rowToEdit }
      : {};
    let hasValidLanguageData = false;

    const selectedTagIds = selectedTags?.map((tag) => tag.id);

    Object.entries(newEvent).forEach(([lang, data]) => {
      if (["en", "fr", "ar"].includes(lang)) {
        const hasData =
          data.title?.trim() ||
          data.brief?.trim() ||
          data.location?.trim() ||
          data.description?.trim() ||
          data.yt_url?.trim();
        if (hasData) {
          hasValidLanguageData = true;
          payload[lang] = {
            selectedTags: selectedTagIds,
            title: data.title?.trim() || "",
            brief: data.brief?.trim() || "",
            location: data.location?.trim() || "",
            description: data.description?.trim() || "",
            yt_url: data.yt_url?.trim() || "",
            date: newEvent.date,
          };
        }
      }
    });

    if (!hasValidLanguageData) {
      alert("Please fill at least one language field before finalizing.");
      return;
    }

    // Attach files based on selected language
    if (newEvent.attachments?.mainImage?.length) {
      files["mainImage"] = newEvent.attachments.mainImage[0].file;
    }

    ["en", "fr", "ar"].forEach((lang) => {
      if (newEvent.attachments?.[`attachment_${lang}`]?.length) {
        files[`attachment_${lang}`] =
          newEvent.attachments[`attachment_${lang}`][0].file;
      }
    });

    // ✅ Only include deleteMainImage if it's true
    if (rowToEdit && newEvent.deleteMainImage) {
      payload.deleteMainImage = true;
    }

    if (rowToEdit && newEvent.deletedAttachments?.length > 0) {
      payload.deletedAttachments = newEvent.deletedAttachments; // Now just an array ["en", "fr"]
    }

    dispatch(saveEvent({ payload, sectionId, files }))
      .unwrap()
      .then(() => {
        dispatch(fetchEvents({ companyId, page, limit: rowsPerPage }));
        setNewEvent({
          en: {
            title: "",
            brief: "",
            location: "",
            description: "",
            yt_url: "",
          },
          fr: {
            title: "",
            brief: "",
            location: "",
            description: "",
            yt_url: "",
          },
          ar: {
            title: "",
            brief: "",
            location: "",
            description: "",
            yt_url: "",
          },
          date: null,
          attachments: {},
        });
        setConfirmationDialogOpen(false);
        setRowToEdit(null);
        setIsAddingTeam(false);
      })
      .catch((error) => {
        console.error("Error saving event:", error);
      });
  };

  const handleExitAndSave = () => {
    handleFinalizeEvent(); // Save the data

    setConfirmationDialogOpen(false); // Close the dialog
  };

  const handleDeleteClick = (id, title) => {
    setPromotionIdToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteTitle(title);
  };

  const handleShowGallery = (id, title) => {
    setGalleryId(id);
    setGalleryDialogOpen(true);
    setDeleteTitle(title);
  };

  const confirmDelete = () => {
    dispatch(deleteSectionData({ id: promotionIdToDelete, sectionId }))
      .then(() => {
        setDeleteDialogOpen(false);
        setPromotionIdToDelete(null);

        const updatedTotalRecords = totalRecords - 1;
        const updatedTotalPages = Math.ceil(updatedTotalRecords / rowsPerPage);

        const newPage = page > updatedTotalPages ? updatedTotalPages : page;

        setPage(newPage);

        dispatch(fetchEvents({ companyId, page: newPage, limit: rowsPerPage }));
      })
      .catch((error) => {
        console.error("Error deleting promotion:", error);
      });
  };

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!isAddingTeam && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Events Section
            </Typography>
            <StyledButton
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddEvent}
              sx={{ marginBottom: 2 }}
              disabled={loading}
            >
              Add
            </StyledButton>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <TextField
                type="number"
                label="Edit by ID"
                variant="outlined"
                value={rowToEdit}
                onChange={(e) => setRowToEdit(e.target.value)}
                size="small"
                sx={{ marginRight: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleEditEvent(parseInt(rowToEdit))}
                disabled={loading || !rowToEdit}
              >
                Edit
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="newsTable">
                  {(provided) => (
                    <Table ref={provided.innerRef} {...provided.droppableProps}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell width="5%"></StyledTableCell>
                          <StyledTableCell
                            width={languagesList.length >= 2 ? "65%" : "80%"}
                          >
                            Title
                          </StyledTableCell>

                          <StyledTableCell width="15%">Actions</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(eventsList?.data || []).map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging
                                    ? "#f5f5f5"
                                    : "inherit",
                                }}
                              >
                                {/* Drag Handle */}
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIndicatorIcon />
                                </TableCell>

                                {/* Title */}
                                <TableCell>{item.title || "N/A"}</TableCell>

                                {/* Actions */}
                                <TableCell>
                                  <IconButton
                                    onClick={() => handleEditEvent(item.id)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteClick(item.id, item.title)
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      handleShowGallery(item.id, item.title)
                                    }
                                  >
                                    <CollectionsIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    </Table>
                  )}
                </Droppable>
              </DragDropContext>
            </TableContainer>
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          </>
        )}

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}> {deleteTitle}</span>? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <GalleryUpload
          galleryDialogOpen={galleryDialogOpen}
          setGalleryDialogOpen={setGalleryDialogOpen}
          deleteTitle={deleteTitle}
          fk_sectionDataId={galleryId}
          companyId={companyId}
        />

        {isAddingTeam && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton onClick={() => setIsAddingTeam(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Event</Typography>
            </Box>
            <Tabs
              value={currentLanguage}
              indicatorColor="primary"
              textColor="primary"
              onChange={(e, newValue) => setCurrentLanguage(newValue)}
            >
              {languagesList?.map((lang) => (
                <Tab key={lang.code} value={lang.code} label={lang.name} />
              ))}
            </Tabs>

            <Tags
              tags={tags}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
            />

            <TextField
              sx={{ mt: 2 }}
              label="Title"
              value={newEvent[currentLanguage]?.title || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "title", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Briefing"
              value={newEvent[currentLanguage]?.brief || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "brief", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Location"
              value={newEvent[currentLanguage]?.location || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "location", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 3 }}
              type="datetime-local"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={newEvent.date}
              onChange={(e) => handleGlobalChange("date", e.target.value)}
              fullWidth
              onMouseDown={(e) => {
                e.preventDefault();
                e.target.showPicker();
              }}
            />

            <TextField
              label="YouTube URL"
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              value={newEvent[currentLanguage]?.yt_url || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "yt_url", e.target.value)
              }
            />

            <ReactQuill
              key={currentLanguage}
              style={{
                marginTop: "20px",
                height: "300px",
                marginBottom: "60px",
              }}
              value={newEvent[currentLanguage]?.description || ""}
              onChange={(value) =>
                handleInputChange(currentLanguage, "description", value)
              }
              theme="snow"
              placeholder="Event description..."
              modules={{
                toolbar: [
                  [{ header: "1" }, { header: "2" }, { font: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["bold", "italic", "underline"],
                  [{ align: [] }],
                  ["link"],
                  [{ color: [] }, { background: [] }],
                  ["clean"],
                ],
              }}
            />

            <Attachments
              onAttachmentsChange={handleAttachmentsChange}
              media={media}
              currentLanguage={currentLanguage}
              onDeleteMainImage={handleDeleteMainImage} // Pass delete flag handler
              onDeleteAttachment={handleDeleteAttachment} // Pass
            />

            <Box sx={{ mt: 8, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setIsAddingTeam(false)}
                sx={{ marginRight: 2 }}
                color="error"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveLanguage}
                color="primary"
                variant="contained"
              >
                Save / Skip
              </Button>
            </Box>

            <Dialog
              open={confirmationDialogOpen}
              onClose={() => setConfirmationDialogOpen(false)}
            >
              <DialogTitle>Proceed to Next Language?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Do you want to proceed to the next language or finalize?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleExitAndSave} color="primary">
                  Exit And Save
                </Button>
                <Button onClick={handleProceedToNextLanguage} color="primary">
                  Next Language
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Paper>
    </StyledContainer>
  );
}
